/**
 * Gemerkte Einstellungen und Besuchszaehler.
 *
 * Nutzereinstellungen liegen unter `iammatrix.`-Schluesseln im lokalen
 * Speicher (`merken` im HTML-Prototyp). Bis zum 17.09.2026 trugen sie das
 * fruehere Kuerzel des Produkts im Praefix; dort gemerkte Werte werden nicht
 * uebernommen. Es sind Thema, Marke und ausgeblendete Kacheln — nichts, das
 * sich nicht mit einem Klick wieder setzen liesse.
 *
 * Faellt der lokale Speicher aus — privater Modus, gesperrte Anwendung —,
 * arbeitet die Oberflaeche mit den Vorgabewerten weiter, ohne zu scheitern.
 */

import { useCallback, useState } from 'react';

/* Einstellungen liegen unter `iammatrix.`; nur der Besuchszaehler nutzt
   `iam.besuch.`. */
const PRAEFIX = 'iammatrix.';

export function gemerkt(schluessel: string): string | null {
  try {
    return localStorage.getItem(PRAEFIX + schluessel);
  } catch {
    return null;
  }
}

export function merken(schluessel: string, wert: string | number | boolean): void {
  try {
    localStorage.setItem(PRAEFIX + schluessel, String(wert));
  } catch {
    /* Kein Speicher verfuegbar — die Einstellung gilt dann nur fuer diese Sitzung. */
  }
}

/**
 * Zustand, der den Neustart ueberdauert. Verhaelt sich wie `useState`,
 * schreibt aber jede Aenderung zusaetzlich in den lokalen Speicher.
 */
export function useGemerkt<T extends string | number | boolean>(
  schluessel: string,
  vorgabe: T,
): [T, (wert: T) => void] {
  const [wert, setzeWert] = useState<T>(() => {
    const roh = gemerkt(schluessel);
    if (roh === null) return vorgabe;
    if (typeof vorgabe === 'number') {
      const zahl = Number(roh);
      return (Number.isFinite(zahl) ? zahl : vorgabe) as T;
    }
    if (typeof vorgabe === 'boolean') return (roh === '1' || roh === 'true') as T;
    return roh as T;
  });

  const setzen = useCallback(
    (neu: T) => {
      setzeWert(neu);
      merken(schluessel, typeof neu === 'boolean' ? (neu ? '1' : '0') : neu);
    },
    [schluessel],
  );

  return [wert, setzen];
}

/**
 * Zusammengesetzte Werte — im Entwurf legt `merken` sie als JSON ab.
 * Gebraucht etwa fuer die Liste ausgeblendeter Spalten (`mxAus`).
 */
export function gemerktJson<T>(schluessel: string, vorgabe: T): T {
  const roh = gemerkt(schluessel);
  if (roh === null) return vorgabe;
  try {
    return JSON.parse(roh) as T;
  } catch {
    return vorgabe;
  }
}

export function merkenJson(schluessel: string, wert: unknown): void {
  try {
    localStorage.setItem(PRAEFIX + schluessel, JSON.stringify(wert));
  } catch {
    /* Kein Speicher verfuegbar — die Einstellung gilt dann nur fuer diese Sitzung. */
  }
}

/** Wie `useGemerkt`, aber fuer Listen und Objekte. */
export function useGemerktJson<T>(schluessel: string, vorgabe: T): [T, (wert: T) => void] {
  const [wert, setzeWert] = useState<T>(() => gemerktJson(schluessel, vorgabe));
  const setzen = useCallback(
    (neu: T) => { setzeWert(neu); merkenJson(schluessel, neu); },
    [schluessel],
  );
  return [wert, setzen];
}

/**
 * Besuchszaehler je Seite (Spezifikation 8.1).
 *
 * Erklaerungen erscheinen bei den ersten drei Besuchen einer Seite von selbst.
 * Der Zaehler steigt einmal je Pfad und Sitzung, nicht bei jedem Rendern —
 * sonst waere die Schwelle nach drei Zustandsaenderungen erreicht statt nach
 * drei Besuchen.
 */
const besucheDieserSitzung: Record<string, number> = {};

export function besuche(pfad: string): number {
  if (besucheDieserSitzung[pfad] == null) {
    let n = 1;
    try {
      n = parseInt(localStorage.getItem('iam.besuch.' + pfad) || '0', 10) + 1;
      localStorage.setItem('iam.besuch.' + pfad, String(n));
    } catch {
      n = 1;
    }
    besucheDieserSitzung[pfad] = n;
  }
  return besucheDieserSitzung[pfad];
}
